import { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Layers,
  PlusCircle,
  GripVertical,
  Edit,
  Trash2,
} from 'lucide-react';
import clsx from 'clsx';
import {
  useAdminCategories,
  useReorderCategories,
} from '../../services/adminService';
import { CategoryFormModal } from '../../components/CategoryFormModal/CategoryFormModal';
import { DeleteCategoryModal } from '../../components/DeleteCategoryModal/DeleteCategoryModal';
import { getCategoryIcon } from '../../lib/categoryIcons';

/**
 * Admin categories management page with drag-to-reorder,
 * add/edit modals, and delete with product reassignment.
 */
export default function AdminCategoriesPage() {
  const { data: categories, isLoading } = useAdminCategories();
  const reorderMutation = useReorderCategories();

  const [modalOpen, setModalOpen] = useState(false);
  const [editCategory, setEditCategory] = useState(null);
  const [deleteCategory, setDeleteCategory] = useState(null);

  // ── Drag-to-reorder state ──
  const [localOrder, setLocalOrder] = useState(null); // null = use server data
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  const displayCategories = localOrder || categories || [];

  const handleDragStart = (index) => {
    dragItem.current = index;
    if (!localOrder) setLocalOrder([...(categories || [])]);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    dragOverItem.current = index;
  };

  const handleDrop = useCallback(() => {
    const from = dragItem.current;
    const to = dragOverItem.current;
    if (from === null || to === null || from === to) return;

    const items = [...(localOrder || categories || [])];
    const [dragged] = items.splice(from, 1);
    items.splice(to, 0, dragged);
    setLocalOrder(items);

    // Persist reorder
    reorderMutation.mutate(items.map((c) => c.id), {
      onSuccess: () => setLocalOrder(null),
      onError: () => setLocalOrder(null),
    });

    dragItem.current = null;
    dragOverItem.current = null;
  }, [localOrder, categories, reorderMutation]);

  const openAdd = () => {
    setEditCategory(null);
    setModalOpen(true);
  };

  const openEdit = (cat) => {
    setEditCategory(cat);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditCategory(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-brand-dark">Categories</h1>
          <p className="text-sm text-brand-gray mt-1">
            {(displayCategories).length} categor{displayCategories.length !== 1 ? 'ies' : 'y'} &bull; Drag to reorder
          </p>
        </div>
        <button
          onClick={openAdd}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-brand-red hover:bg-red-700 text-brand-white text-sm font-bold rounded-xl transition-colors"
        >
          <PlusCircle size={16} />
          Add Category
        </button>
      </div>

      {/* Category list */}
      <div className="bg-brand-white border border-ui-border rounded-2xl overflow-hidden">
        {isLoading ? (
          <div className="p-8 space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-4 animate-pulse">
                <div className="w-5 h-8 bg-brand-light rounded" />
                <div className="w-10 h-10 bg-brand-light rounded-xl" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-brand-light rounded w-1/3" />
                  <div className="h-3 bg-brand-light rounded w-1/4" />
                </div>
              </div>
            ))}
          </div>
        ) : displayCategories.length === 0 ? (
          <div className="p-12 text-center">
            <Layers size={40} className="text-ui-muted mx-auto mb-3" />
            <p className="text-sm font-medium text-brand-gray">No categories yet</p>
            <button
              onClick={openAdd}
              className="mt-3 text-sm font-bold text-brand-red hover:underline"
            >
              Create your first category
            </button>
          </div>
        ) : (
          <div className="divide-y divide-ui-border">
            <AnimatePresence mode="popLayout">
              {displayCategories.map((cat, i) => (
                <motion.div
                  key={cat.id}
                  layout
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0, x: -40, transition: { duration: 0.2 } }}
                  transition={{ delay: i * 0.02 }}
                  draggable
                  onDragStart={() => handleDragStart(i)}
                  onDragOver={(e) => handleDragOver(e, i)}
                  onDrop={handleDrop}
                  onDragEnd={() => { dragItem.current = null; dragOverItem.current = null; }}
                  className="flex items-center gap-4 p-4 hover:bg-brand-light/50 transition-colors group"
                >
                  {/* Drag handle */}
                  <div className="cursor-grab active:cursor-grabbing text-ui-muted group-hover:text-brand-gray transition-colors flex-shrink-0">
                    <GripVertical size={18} />
                  </div>

                  {/* Icon */}
                  <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
                    {(() => {
                      const CatIcon = getCategoryIcon(cat.icon);
                      return <CatIcon size={18} className="text-amber-600" />;
                    })()}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-bold text-brand-dark truncate">{cat.name}</h3>
                    <p className="text-xs text-brand-gray mt-0.5">/{cat.slug}</p>
                  </div>

                  {/* Order badge */}
                  <span className="text-xs font-bold text-ui-muted bg-brand-light px-2 py-1 rounded-lg flex-shrink-0">
                    #{i + 1}
                  </span>

                  {/* Actions */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => openEdit(cat)}
                      className="p-2 rounded-lg text-brand-gray hover:bg-blue-50 hover:text-blue-600 transition-colors"
                      aria-label="Edit"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={() => setDeleteCategory(cat)}
                      className="p-2 rounded-lg text-brand-gray hover:bg-red-50 hover:text-error transition-colors"
                      aria-label="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* Add / Edit modal */}
      <CategoryFormModal
        open={modalOpen}
        category={editCategory}
        onClose={closeModal}
      />

      {/* Delete modal */}
      <DeleteCategoryModal
        category={deleteCategory}
        onClose={() => setDeleteCategory(null)}
      />
    </div>
  );
}
